/* eslint-disable no-nested-ternary */
import {
    ArrowDownwardOutlined,
    Delete,
    InboxTwoTone,
    MoreVertTwoTone,
    ContentCopy,
} from "@mui/icons-material";
import {
    IconButton,
    ListItemIcon,
    MenuItem,
    Popover,
    Typography,
    Table as MuiTable,
    TableCell,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { LoadingGray } from "../../../components";
import Tag from "../../../components/atoms/tag/tag";
import {
    arquiveBudget,
    deleteBudget,
    unarquiveBudget,
} from "../../../services/budget";
import { formatHoursToTime } from "../../../utils/formatHoursToTime/formatHoursToTime";
import { formatCurrency } from "../../organization/project/tab-costs/utils";
import { NoBudget } from "./tab-budget";

export type DisplayedBudgetStatus =
    | "Andamento"
    | "Aprovado"
    | "Aguardando"
    | "Reprovado";

type BudgetForTable = {
    id: string;
    name: string;
    created_at: string;
    status: string;
    client: string;
    hours?: number;
    cost?: number;
    type: string;
    color: string;
    secondary_color: string;
    color_status: string;
    isArchived: boolean;
};

type TBudgetTable = {
    budgets: BudgetForTable[];
    isLoadingPage: boolean;
    refetchBudget: () => void;
};

const ArrowdownStyle = {
    width: "1.125rem",
    height: "1.125rem",
};

const parsePtBrDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day);
};

export const Table = styled(MuiTable)(({ theme }) => ({
    width: "100%",
    marginTop: "1.5rem",
    borderCollapse: "collapse",
    tableLayout: "auto",

    "& thead tr:first-of-type": {
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    },

    "& tbody tr": {
        border: "1px solid rgba(0, 0, 0, 0.12)",
        "&.hoverable-row:hover": {
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
            transition: "background 0.3s",
        },
    },

    "& th, & td": {
        padding: "1rem",
        color: "var(--text-primary, rgba(62, 55, 78, 0.87))",
        fontFamily: "Noto Sans",
        fontSize: "0.875rem",
        fontStyle: "normal",
        lineHeight: "1.25rem",
        letterSpacing: "0.0088rem",
        textAlign: "left",
        whiteSpace: "nowrap",
        width: "16%",
    },

    "& th:last-of-type, & td:last-of-type": {
        textAlign: "right",
        width: "1%",
    },

    "& th": {
        fontWeight: 700,
    },

    "& td": {
        fontWeight: 400,
    },
}));

export const BudgetTable = ({
    budgets,
    isLoadingPage,
    refetchBudget,
}: TBudgetTable) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: "asc" | "desc";
    } | null>({ key: "created_at", direction: "desc" }); // padrão: mais recente primeiro
    const [selectedBudget, setSelectedBudget] = useState<string | null>(null);

    const navigate = useNavigate();
    const handleRedirectToBudgetModules = (id: string) =>
        navigate(`/financial/budget/modules/${id}`);

    const handleOpen = (
        event: React.MouseEvent<HTMLElement>,
        budgetId: string
    ) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedBudget(budgetId);
    };

    const handleClose = (event: React.MouseEvent) => {
        event.stopPropagation();
        setAnchorEl(null);
        setSelectedBudget(null);
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev?.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleArquiveBudget = async () => {
        if (selectedBudget) {
            await arquiveBudget({ budgetId: selectedBudget });
            await refetchBudget();
            toast.success("Orçamento arquivado com sucesso!");
        }
    };

    const handleUnarquiveBudget = async () => {
        if (selectedBudget) {
            await unarquiveBudget({ budgetId: selectedBudget });
            await refetchBudget();
            toast.success("Orçamento desarquivado com sucesso!");
        }
    };

    const hadleDeleteBudget = async () => {
        if (selectedBudget) {
            await deleteBudget({ budgetId: selectedBudget });
            await refetchBudget();
            toast.success("Orçamento deletado com sucesso!");
        }
    };

    const handleDuplicateBudget = async () => {
        if (selectedBudget) {
            navigate(`/financial/budget/duplicate/${selectedBudget}`);
        }
    };

    const sortedBudgets = [...budgets].sort((a, b) => {
        if (!sortConfig) return 0;
        const { key, direction } = sortConfig;

        const valueA = a[key as keyof typeof a];
        const valueB = b[key as keyof typeof b];

        if (key === "created_at") {
            const dateA = parsePtBrDate(String(valueA));
            const dateB = parsePtBrDate(String(valueB));
            return direction === "asc"
                ? dateA.getTime() - dateB.getTime()
                : dateB.getTime() - dateA.getTime();
        }

        if (typeof valueA === "number" && typeof valueB === "number") {
            return direction === "asc" ? valueA - valueB : valueB - valueA;
        }

        if (typeof valueA === "string" && typeof valueB === "string") {
            return direction === "asc"
                ? valueA.localeCompare(valueB)
                : valueB.localeCompare(valueA);
        }

        return 0;
    });

    return (
        <Table>
            <tr>
                <th>Nome</th>
                <th>
                    Data{" "}
                    <IconButton
                        sx={{ p: "0.0625rem" }}
                        onClick={() => handleSort("created_at")}
                    >
                        <ArrowDownwardOutlined
                            sx={{
                                ...ArrowdownStyle,
                                transform:
                                    sortConfig?.key === "created_at" &&
                                    sortConfig.direction === "asc"
                                        ? "rotate(180deg)"
                                        : "none",
                                transition: "0.2s",
                            }}
                        />
                    </IconButton>
                </th>
                <th>Status</th>
                <th>
                    Horas{" "}
                    <IconButton
                        sx={{ p: "0.0625rem" }}
                        onClick={() => handleSort("hours")}
                    >
                        <ArrowDownwardOutlined
                            sx={{
                                ...ArrowdownStyle,
                                transform:
                                    sortConfig?.key === "hours" &&
                                    sortConfig.direction === "asc"
                                        ? "rotate(180deg)"
                                        : "none",
                                transition: "0.2s",
                            }}
                        />
                    </IconButton>
                </th>
                <th>
                    Custo{" "}
                    <IconButton
                        sx={{ p: "0.0625rem" }}
                        onClick={() => handleSort("cost")}
                    >
                        <ArrowDownwardOutlined
                            sx={{
                                ...ArrowdownStyle,
                                transform:
                                    sortConfig?.key === "cost" &&
                                    sortConfig.direction === "asc"
                                        ? "rotate(180deg)"
                                        : "none",
                                transition: "0.2s",
                            }}
                        />
                    </IconButton>
                </th>
                <th>Cliente</th>
                <TableCell
                    sx={{
                        textAlign: "center !important",
                        fontWeight: "700 !important",
                        borderBottom: "none",
                    }}
                >
                    Tipo
                </TableCell>
                <th></th>
            </tr>
            {isLoadingPage ? (
                <tr>
                    <TableCell colSpan={7} width="100%" height="60vh">
                        <LoadingGray />
                    </TableCell>
                </tr>
            ) : budgets?.length === 0 ? (
                <NoBudget />
            ) : (
                sortedBudgets.map((budget) => (
                    <TableRow
                        key={budget.id}
                        onClick={() => handleRedirectToBudgetModules(budget.id)}
                        sx={{
                            "&:hover": {
                                backgroundColor: "#f5f5f5",
                                cursor: "pointer",
                            },
                        }}
                    >
                        <td>
                            <Tag
                                maxWidth="14.75rem"
                                color={budget.color}
                                isFavorite={false}
                            >
                                {budget.name}
                            </Tag>
                        </td>
                        <td>{budget.created_at}</td>
                        <td>
                            <Typography
                                component="strong"
                                fontWeight="600"
                                color={budget.color_status}
                                fontFamily="Noto Sans"
                                lineHeight="1.25rem"
                                fontSize="12px"
                            >
                                {budget.status}
                            </Typography>
                        </td>
                        <td>
                            {formatHoursToTime(
                                Number(budget?.hours?.toFixed(2)) ?? 0
                            )}
                        </td>
                        <td>
                            {formatCurrency(String(budget.cost?.toFixed(2)))}
                        </td>
                        <td>{budget.client}</td>
                        <TableCell
                            sx={{
                                textAlign: "center !important",
                                borderBottom: "none",
                            }}
                        >
                            {budget.type}
                        </TableCell>

                        <td>
                            <IconButton
                                onClick={(e) => handleOpen(e, budget.id)}
                            >
                                <MoreVertTwoTone />
                            </IconButton>
                        </td>
                        <Popover
                            open={
                                Boolean(anchorEl) &&
                                selectedBudget === budget.id
                            }
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDuplicateBudget();
                                }}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography variant="body1">
                                    Duplicar Orçamento
                                </Typography>
                                <ListItemIcon sx={{ p: "0.5rem" }}>
                                    <ContentCopy fontSize="small" />
                                </ListItemIcon>
                            </MenuItem>
                            <MenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    hadleDeleteBudget();
                                }}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography variant="body1">
                                    Excluir Orçamento
                                </Typography>
                                <ListItemIcon sx={{ p: "0.5rem" }}>
                                    <Delete
                                        fontSize="small"
                                        sx={{ color: "red" }}
                                    />
                                </ListItemIcon>
                            </MenuItem>
                            {budget.isArchived ? (
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarquiveBudget();
                                    }}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Typography variant="body1">
                                        Desarquivar Orçamento
                                    </Typography>
                                    <ListItemIcon sx={{ p: "0.5rem" }}>
                                        <InboxTwoTone fontSize="small" />
                                    </ListItemIcon>
                                </MenuItem>
                            ) : (
                                <MenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleArquiveBudget();
                                    }}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Typography variant="body1">
                                        Arquivar Orçamento
                                    </Typography>
                                    <ListItemIcon sx={{ p: "0.5rem" }}>
                                        <InboxTwoTone fontSize="small" />
                                    </ListItemIcon>
                                </MenuItem>
                            )}
                        </Popover>
                    </TableRow>
                ))
            )}
        </Table>
    );
};
